import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bell, BellOff, Sun, Moon, Clock } from "lucide-react";
import { Card, Button } from "./index";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_OPTIONS = [
  { 
    id: "morning", 
    label: "Morning", 
    time: "8:00 AM",
    icon: Sun,
    description: "Start your day with intention",
    gradient: "from-[#FDF6E3] to-[#F5E6C8]"
  },
  { 
    id: "midday", 
    label: "Midday", 
    time: "12:00 PM",
    icon: Clock,
    description: "A mindful pause in your day",
    gradient: "from-[#E8F4EA] to-[#D4E6D7]"
  },
  { 
    id: "evening", 
    label: "Evening", 
    time: "7:00 PM",
    icon: Moon,
    description: "Wind down with presence",
    gradient: "from-[#E8E4F0] to-[#D5D0E3]"
  }
];

export const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    reminder_enabled: false,
    reminder_time: "morning"
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);

  const getToken = () => localStorage.getItem("reclaim_token");

  // Check push notification support
  useEffect(() => {
    const checkPushSupport = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        setPushSupported(true);
        
        // Check if already subscribed
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setPushSubscribed(!!subscription);
        } catch (err) {
          console.log("Error checking push subscription:", err);
        }
      }
    };
    checkPushSupport();
  }, []);

  // Load current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API_URL}/api/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        
        if (data.notification_settings) {
          setSettings(data.notification_settings);
        }
      } catch (err) {
        console.log("Error loading settings:", err);
      }
      setLoading(false);
    };
    fetchSettings();
  }, []);

  // Register service worker
  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) return null;
    
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration.scope);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      return null;
    }
  };

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    try {
      // Get VAPID key from backend
      const token = getToken();
      const vapidRes = await fetch(`${API_URL}/api/push/vapid-key`);
      const { vapid_key, enabled } = await vapidRes.json();
      
      if (!enabled || !vapid_key) {
        // Push not configured on server, but still save preference
        return true;
      }
      
      // Register service worker
      const registration = await registerServiceWorker();
      if (!registration) return false;
      
      await navigator.serviceWorker.ready;
      
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Please allow notifications in your browser settings");
        return false;
      }
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid_key)
      });
      
      // Send subscription to backend
      await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(subscription.getKey('auth'))
          }
        })
      });
      
      setPushSubscribed(true);
      return true;
    } catch (err) {
      console.error("Push subscription error:", err);
      return false;
    }
  };

  // Unsubscribe from push
  const unsubscribeFromPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        
        const token = getToken();
        await fetch(`${API_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setPushSubscribed(false);
    } catch (err) {
      console.error("Push unsubscribe error:", err);
    }
  };

  // Toggle reminder
  const toggleReminder = async () => {
    const newEnabled = !settings.reminder_enabled;
    
    if (newEnabled && pushSupported) {
      const success = await subscribeToPush();
      if (!success && pushSupported) {
        // Only show error if push is supported but failed
        // Still save preference even if push fails
      }
    } else if (!newEnabled && pushSubscribed) {
      await unsubscribeFromPush();
    }
    
    setSettings(prev => ({ ...prev, reminder_enabled: newEnabled }));
    await saveSettings({ ...settings, reminder_enabled: newEnabled });
  };

  // Update reminder time
  const selectTime = async (timeId) => {
    setSettings(prev => ({ ...prev, reminder_time: timeId }));
    await saveSettings({ ...settings, reminder_time: timeId });
  };

  // Save settings to backend
  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          notification_settings: newSettings
        })
      });
      toast.success("Settings saved");
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6" data-testid="notification-settings">
      <div className="space-y-6">
        {/* Header with Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              settings.reminder_enabled 
                ? "bg-[#4A6741]/10" 
                : "bg-gray-100"
            }`}>
              {settings.reminder_enabled 
                ? <Bell size={20} className="text-[#4A6741]" />
                : <BellOff size={20} className="text-gray-400" />
              }
            </div>
            <div>
              <h3 className="font-heading text-lg text-[#1F2937]">Daily Ritual Reminder</h3>
              <p className="text-sm text-[#6B7280]">
                {settings.reminder_enabled 
                  ? "You'll receive gentle reminders" 
                  : "Enable to never miss your ritual"
                }
              </p>
            </div>
          </div>
          
          {/* Premium Toggle Switch */}
          <button
            onClick={toggleReminder}
            disabled={saving}
            className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
              settings.reminder_enabled 
                ? "bg-[#4A6741]" 
                : "bg-gray-300"
            }`}
            data-testid="reminder-toggle"
          >
            <motion.div
              animate={{ x: settings.reminder_enabled ? 28 : 2 }}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
              className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-md"
            />
          </button>
        </div>

        {/* Time Selection - only show when enabled */}
        {settings.reminder_enabled && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-3"
          >
            <p className="text-sm text-[#6B7280]">When would you like to be reminded?</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {TIME_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = settings.reminder_time === option.id;
                
                return (
                  <motion.button
                    key={option.id}
                    onClick={() => selectTime(option.id)}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? "border-[#4A6741] bg-gradient-to-br " + option.gradient
                        : "border-[#E5E7EB] hover:border-[#D4A373]/50"
                    }`}
                    data-testid={`time-option-${option.id}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Icon size={18} className={isSelected ? "text-[#4A6741]" : "text-[#6B7280]"} />
                      <span className={`font-medium ${isSelected ? "text-[#1F2937]" : "text-[#6B7280]"}`}>
                        {option.label}
                      </span>
                    </div>
                    <p className={`text-lg font-heading ${isSelected ? "text-[#1F2937]" : "text-[#6B7280]"}`}>
                      {option.time}
                    </p>
                    <p className={`text-xs mt-1 ${isSelected ? "text-[#4A6741]" : "text-[#9CA3AF]"}`}>
                      {option.description}
                    </p>
                    
                    {/* Selected indicator */}
                    {isSelected && (
                      <motion.div
                        layoutId="time-indicator"
                        className="absolute top-2 right-2 w-5 h-5 bg-[#4A6741] rounded-full flex items-center justify-center"
                      >
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </motion.div>
                    )}
                  </motion.button>
                );
              })}
            </div>
            
            {/* Push notification status */}
            {pushSupported && (
              <p className="text-xs text-[#9CA3AF] text-center mt-4">
                {pushSubscribed 
                  ? "Push notifications enabled for this device"
                  : "Browser notifications will be requested when available"
                }
              </p>
            )}
          </motion.div>
        )}
      </div>
    </Card>
  );
};

// Helper functions for push subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export default NotificationSettings;
